import { useState, useRef, useMemo, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Boxes, Plus, CheckCircle, Tag, Trash2, Image as ImageIcon, Camera, Upload, Pencil, X } from 'lucide-react'
import { b2bLotsApi, B2BLot, B2BUnit, B2BPublishStatus } from '@/lib/api'
import { useToast } from '@/components/ui/Toast'
import Modal from '@/components/ui/Modal'
import Button from '@/components/ui/Button'
import Input from '@/components/ui/Input'

const emptyLot = {
  name: '', model: '', specs: '', purchase_total_cost: 0,
  margin_eur: 50, warranty_months: 3, charger_included: false, keyboard_layout: '',
}
const emptyUnit = {
  sticker_id: '', model: '', specs: '', keyboard_layout: '', unit_cost: '' as number | '', unit_price: '' as number | '',
  defects: '', charger_included: false, warranty_months: '' as number | '',
}
type UnitForm = typeof emptyUnit

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, string> = {
    draft: 'bg-gray-100 text-gray-700', published: 'bg-green-100 text-green-700',
    closed: 'bg-blue-100 text-blue-700', available: 'bg-green-100 text-green-700',
    sold: 'bg-blue-100 text-blue-700', reserved: 'bg-amber-100 text-amber-700',
    withdrawn: 'bg-red-100 text-red-700',
  }
  return <span className={`px-2 py-0.5 rounded text-xs font-medium ${map[status] || 'bg-gray-100 text-gray-700'}`}>{status}</span>
}

// Miniatura de una foto nueva (File en memoria), con object URL gestionado
function FileThumb({ file, onRemove }: { file: File; onRemove: () => void }) {
  const url = useMemo(() => URL.createObjectURL(file), [file])
  useEffect(() => () => URL.revokeObjectURL(url), [url])
  return (
    <div className="relative">
      <img src={url} className="w-16 h-16 object-cover rounded border" />
      <button type="button" onClick={onRemove}
        className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center">
        <X className="w-3 h-3" />
      </button>
    </div>
  )
}

// Miniatura de una foto ya guardada en borrador (se descarga del backend como blob)
function DraftThumb({ unitId, idx, onDelete }: { unitId: number; idx: number; onDelete: () => void }) {
  const { data: url } = useQuery({
    queryKey: ['b2b-unit-img', unitId, idx],
    queryFn: () => b2bLotsApi.getUnitImage(unitId, idx),
    staleTime: 60_000,
  })
  useEffect(() => () => { if (url) URL.revokeObjectURL(url) }, [url])
  return (
    <div className="relative">
      {url
        ? <img src={url} className="w-16 h-16 object-cover rounded border" />
        : <div className="w-16 h-16 rounded border bg-gray-100 animate-pulse" />}
      <button type="button" onClick={onDelete}
        className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center">
        <X className="w-3 h-3" />
      </button>
    </div>
  )
}

// Selector de fotos: botón cámara (móvil) + botón galería, con previews
function PhotoPicker({ files, setFiles }: { files: File[]; setFiles: (f: File[]) => void }) {
  const camRef = useRef<HTMLInputElement>(null)
  const galRef = useRef<HTMLInputElement>(null)
  const add = (list: FileList | null) => { if (list) setFiles([...files, ...Array.from(list)]) }
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">Fotos reales del equipo</label>
      <div className="flex gap-2">
        <Button type="button" variant="outline" leftIcon={<Camera className="w-4 h-4" />} onClick={() => camRef.current?.click()}>Hacer foto</Button>
        <Button type="button" variant="outline" leftIcon={<Upload className="w-4 h-4" />} onClick={() => galRef.current?.click()}>Adjuntar</Button>
      </div>
      <input ref={camRef} type="file" accept="image/*" capture="environment" multiple className="hidden"
        onChange={(e) => { add(e.target.files); e.currentTarget.value = '' }} />
      <input ref={galRef} type="file" accept="image/*" multiple className="hidden"
        onChange={(e) => { add(e.target.files); e.currentTarget.value = '' }} />
      {files.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-2">
          {files.map((f, i) => <FileThumb key={i} file={f} onRemove={() => setFiles(files.filter((_, j) => j !== i))} />)}
        </div>
      )}
    </div>
  )
}

export default function B2BLotsPage() {
  const toast = useToast()
  const qc = useQueryClient()
  const [showCreate, setShowCreate] = useState(false)
  const [selectedLotId, setSelectedLotId] = useState<number | null>(null)
  const [showAddUnit, setShowAddUnit] = useState(false)
  const [lotForm, setLotForm] = useState({ ...emptyLot })
  const [unitForm, setUnitForm] = useState<UnitForm>({ ...emptyUnit })
  const [files, setFiles] = useState<File[]>([])
  // Edición de unidad
  const [editUnit, setEditUnit] = useState<B2BUnit | null>(null)
  const [editForm, setEditForm] = useState<UnitForm>({ ...emptyUnit })
  const [editFiles, setEditFiles] = useState<File[]>([])

  const lotsQuery = useQuery({ queryKey: ['b2b-lots'], queryFn: b2bLotsApi.list })
  const detailQuery = useQuery({
    queryKey: ['b2b-lot', selectedLotId],
    queryFn: () => b2bLotsApi.get(selectedLotId!),
    enabled: selectedLotId != null,
  })
  const lot: B2BLot | undefined = detailQuery.data?.lot
  const isDraft = lot?.status !== 'published'

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['b2b-lot', selectedLotId] })
    qc.invalidateQueries({ queryKey: ['b2b-lots'] })
  }

  const createMut = useMutation({
    mutationFn: () => b2bLotsApi.create(lotForm),
    onSuccess: (res) => {
      toast.success('Lote creado')
      qc.invalidateQueries({ queryKey: ['b2b-lots'] })
      setShowCreate(false); setLotForm({ ...emptyLot })
      setSelectedLotId(res.lot.id)
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Error al crear el lote'),
  })

  const addUnitMut = useMutation({
    mutationFn: () => b2bLotsApi.addUnit(selectedLotId!, {
      sticker_id: unitForm.sticker_id,
      model: unitForm.model || undefined,
      specs: unitForm.specs || undefined,
      keyboard_layout: unitForm.keyboard_layout || undefined,
      unit_cost: unitForm.unit_cost === '' ? undefined : Number(unitForm.unit_cost),
      unit_price: unitForm.unit_price === '' ? undefined : Number(unitForm.unit_price),
      defects: unitForm.defects || undefined,
      charger_included: unitForm.charger_included,
      warranty_months: unitForm.warranty_months === '' ? undefined : Number(unitForm.warranty_months),
    }, files),
    onSuccess: () => {
      toast.success('Unidad añadida al borrador')
      invalidate()
      // mantener el modal abierto para añadir otra; conservar plantilla y coste/garantía
      setUnitForm((u) => ({ ...emptyUnit, model: u.model, specs: u.specs, keyboard_layout: u.keyboard_layout, unit_cost: u.unit_cost, warranty_months: u.warranty_months }))
      setFiles([])
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Error al añadir la unidad'),
  })

  const updateUnitMut = useMutation({
    mutationFn: async () => {
      await b2bLotsApi.updateUnit(editUnit!.id, {
        model: editForm.model || undefined,
        specs: editForm.specs || undefined,
        keyboard_layout: editForm.keyboard_layout || undefined,
        sticker_id: editForm.sticker_id || undefined,
        unit_cost: editForm.unit_cost === '' ? undefined : Number(editForm.unit_cost),
        unit_price: editForm.unit_price === '' ? undefined : Number(editForm.unit_price),
        defects: editForm.defects || undefined,
        charger_included: editForm.charger_included,
        warranty_months: editForm.warranty_months === '' ? undefined : Number(editForm.warranty_months),
      })
      if (editFiles.length) await b2bLotsApi.addUnitImages(editUnit!.id, editFiles)
    },
    onSuccess: () => {
      toast.success('Unidad actualizada')
      invalidate(); setEditUnit(null); setEditFiles([])
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Error al actualizar la unidad'),
  })

  const deleteUnitMut = useMutation({
    mutationFn: (unitId: number) => b2bLotsApi.deleteUnit(unitId),
    onSuccess: () => { toast.success('Unidad eliminada'); invalidate() },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Error al eliminar la unidad'),
  })

  const deleteImageMut = useMutation({
    mutationFn: ({ unitId, idx }: { unitId: number; idx: number }) => b2bLotsApi.deleteUnitImage(unitId, idx),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['b2b-unit-img', vars.unitId] })
      invalidate()
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Error al borrar la foto'),
  })

  // Publicación en segundo plano: arranca el proceso y luego se sigue por polling
  const publishMut = useMutation({
    mutationFn: () => b2bLotsApi.publish(selectedLotId!),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['b2b-publish-status', selectedLotId] }) },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Error al iniciar la publicación'),
  })

  // Estado de la publicación (se refresca solo mientras está en curso)
  const pubQuery = useQuery({
    queryKey: ['b2b-publish-status', selectedLotId],
    queryFn: () => b2bLotsApi.publishStatus(selectedLotId!),
    enabled: selectedLotId != null,
    refetchInterval: (q: any) => (q.state.data?.status?.status === 'running' ? 3000 : false),
  })
  const pub: B2BPublishStatus | undefined = pubQuery.data?.status
  const isPublishing = pub?.status === 'running' || publishMut.isPending

  // Detectar el fin de la publicación (running -> done/error) para avisar y refrescar
  const prevPubStatus = useRef<string | null | undefined>(undefined)
  useEffect(() => {
    const prev = prevPubStatus.current
    if (prev === 'running' && pub?.status === 'done') {
      toast.success(`Lote publicado: ${pub.done}/${pub.total} fichas creadas y activas`)
      invalidate()
    } else if (prev === 'running' && pub?.status === 'error') {
      toast.error(`La publicación falló: ${pub.error || 'error desconocido'}`)
      invalidate()
    }
    prevPubStatus.current = pub?.status
  }, [pub?.status])

  const openEdit = (u: B2BUnit) => {
    setEditUnit(u)
    setEditForm({
      sticker_id: u.sticker_id || '', model: u.model || '', specs: u.specs || '',
      keyboard_layout: u.keyboard_layout || '',
      unit_cost: (u.unit_cost ?? '') as number | '', unit_price: (u.unit_price ?? '') as number | '',
      defects: u.defects || '', charger_included: !!u.charger_included,
      warranty_months: (u.warranty_months ?? '') as number | '',
    })
    setEditFiles([])
  }

  const lots = lotsQuery.data?.lots || []
  const suggestedPrice = (() => {
    const cost = unitForm.unit_cost === '' ? (lot?.purchase_total_cost && lot?.units_total ? lot.purchase_total_cost / lot.units_total : 0) : Number(unitForm.unit_cost)
    const margin = lot?.margin_eur ?? 50
    return Math.round((cost + margin) * 100) / 100
  })()

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Boxes className="w-7 h-7 text-primary-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Outlet Pro · Lotes B2B</h1>
            <p className="text-sm text-gray-500">Equipos de lote para distribuidores y VIP</p>
          </div>
        </div>
        <Button leftIcon={<Plus className="w-4 h-4" />} onClick={() => setShowCreate(true)}>Crear lote</Button>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 dark:bg-gray-900 text-gray-500 text-left">
            <tr>
              <th className="px-4 py-3">Lote</th>
              <th className="px-4 py-3">Modelo</th>
              <th className="px-4 py-3 text-center">Uds</th>
              <th className="px-4 py-3 text-center">Vendidas</th>
              <th className="px-4 py-3 text-right">Coste</th>
              <th className="px-4 py-3 text-center">Estado</th>
            </tr>
          </thead>
          <tbody>
            {lots.map((l) => (
              <tr key={l.id} className="border-t border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/40 cursor-pointer"
                onClick={() => setSelectedLotId(l.id)}>
                <td className="px-4 py-3 font-medium">{l.name}<div className="text-xs text-gray-400">{l.lot_code}</div></td>
                <td className="px-4 py-3 text-gray-600">{l.model || '—'}</td>
                <td className="px-4 py-3 text-center">{l.units_total}</td>
                <td className="px-4 py-3 text-center">{l.units_sold}</td>
                <td className="px-4 py-3 text-right">{l.purchase_total_cost?.toFixed(2)} €</td>
                <td className="px-4 py-3 text-center"><StatusBadge status={l.status} /></td>
              </tr>
            ))}
            {lots.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-gray-400">No hay lotes todavía. Crea el primero.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Crear lote */}
      <Modal isOpen={showCreate} onClose={() => setShowCreate(false)} title="Crear lote" size="lg">
        <div className="space-y-3">
          <div className="rounded-lg border border-blue-200 bg-blue-50 dark:border-blue-900 dark:bg-blue-900/20 p-3 text-sm text-blue-800 dark:text-blue-200">
            <p className="font-medium mb-1">Modelo, especificaciones y teclado = plantilla del lote</p>
            <p>Lo que pongas aquí se <span className="font-medium">copia automáticamente a cada unidad</span> al añadirla, y podrás editarlo si ese equipo concreto es distinto.</p>
            <ul className="list-disc list-inside space-y-0.5 mt-1">
              <li><span className="font-medium">Lote de un solo modelo:</span> rellénalos y las unidades vendrán ya completas.</li>
              <li><span className="font-medium">Lote mixto:</span> déjalos vacíos (o pon lo más común) y ajusta modelo/specs/teclado en cada unidad.</li>
            </ul>
          </div>
          <Input label="Nombre del lote" value={lotForm.name} onChange={(e) => setLotForm({ ...lotForm, name: e.target.value })} placeholder="ProBook 640 G8 · jun-2026" />
          <Input label="Modelo (plantilla, opcional)" value={lotForm.model} onChange={(e) => setLotForm({ ...lotForm, model: e.target.value })} placeholder="HP ProBook 640 G8" helperText="Se prerrellena en cada unidad; podrás cambiarlo allí." />
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Especificaciones (plantilla, opcional)</label>
            <textarea className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" rows={2}
              value={lotForm.specs} onChange={(e) => setLotForm({ ...lotForm, specs: e.target.value })}
              placeholder="i5-1145G7 · 16 GB · 256 GB SSD NVMe · 14&quot; · Windows" />
            <p className="text-xs text-gray-500 mt-1">Se prerrellena en cada unidad. Si los equipos tienen CPU/RAM/disco distintos, ajústalo en cada una.</p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Input label="Coste total de compra (€)" type="number" value={lotForm.purchase_total_cost}
              onChange={(e) => setLotForm({ ...lotForm, purchase_total_cost: Number(e.target.value) })} />
            <Input label="Margen sugerido (€/ud)" type="number" value={lotForm.margin_eur}
              onChange={(e) => setLotForm({ ...lotForm, margin_eur: Number(e.target.value) })} />
            <Input label="Garantía (meses)" type="number" value={lotForm.warranty_months}
              onChange={(e) => setLotForm({ ...lotForm, warranty_months: Number(e.target.value) })} />
            <Input label="Teclado (plantilla)" value={lotForm.keyboard_layout}
              onChange={(e) => setLotForm({ ...lotForm, keyboard_layout: e.target.value })} placeholder="Español (QWERTY) · Francés (AZERTY)" />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={lotForm.charger_included} onChange={(e) => setLotForm({ ...lotForm, charger_included: e.target.checked })} />
            Incluye cargador
          </label>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={() => setShowCreate(false)}>Cancelar</Button>
            <Button loading={createMut.isPending} disabled={!lotForm.name} onClick={() => createMut.mutate()}>Crear lote</Button>
          </div>
        </div>
      </Modal>

      {/* Detalle del lote */}
      <Modal isOpen={selectedLotId != null} onClose={() => setSelectedLotId(null)} title={lot?.name || 'Lote'} size="full">
        {detailQuery.isLoading || !lot ? (
          <div className="py-10 text-center text-gray-400">Cargando…</div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-4 text-sm text-gray-600">
                <span className="font-mono">{lot.lot_code}</span>
                <StatusBadge status={lot.status} />
                <span>Factusol: <span className="font-mono">{lot.factusol_codart}</span></span>
              </div>
              <div className="flex flex-col items-end gap-1">
                <div className="flex gap-2">
                  {isDraft && (
                    <Button variant="outline" leftIcon={<Plus className="w-4 h-4" />} disabled={isPublishing} onClick={() => { setUnitForm({ ...emptyUnit, model: lot.model || '', specs: lot.specs || '', keyboard_layout: lot.keyboard_layout || '' }); setFiles([]); setShowAddUnit(true) }}>Añadir unidad</Button>
                  )}
                  <Button leftIcon={<CheckCircle className="w-4 h-4" />} loading={isPublishing}
                    disabled={!lot.units || lot.units.length === 0 || !isDraft || isPublishing}
                    onClick={() => publishMut.mutate()}>{isDraft ? (isPublishing ? 'Publicando…' : 'Publicar lote') : 'Publicado'}</Button>
                </div>
                {isDraft && !isPublishing && (
                  <p className="text-xs text-gray-400">Al publicar se genera la ficha de cada equipo con IA (traducida). El proceso corre en segundo plano; puedes seguir el progreso aquí.</p>
                )}
              </div>
            </div>

            {/* Barra de progreso de publicación */}
            {pub?.status === 'running' && (
              <div className="rounded-lg border border-primary-200 bg-primary-50 dark:border-primary-900 dark:bg-primary-900/20 p-3">
                <div className="flex items-center justify-between text-sm mb-2">
                  <span className="font-medium text-primary-800 dark:text-primary-200">Publicando fichas con IA… {pub.done}/{pub.total}</span>
                  <span className="text-primary-600">{pub.total ? Math.round((pub.done / pub.total) * 100) : 0}%</span>
                </div>
                <div className="w-full h-2 bg-primary-100 dark:bg-primary-900/40 rounded-full overflow-hidden">
                  <div className="h-full bg-primary-600 transition-all duration-500" style={{ width: `${pub.total ? (pub.done / pub.total) * 100 : 0}%` }} />
                </div>
                <p className="text-xs text-primary-600/80 mt-1.5">Puedes cerrar esta ventana; el proceso continúa en el servidor.</p>
              </div>
            )}
            {pub?.status === 'error' && isDraft && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                La última publicación falló ({pub.error || 'error'}). Puedes volver a pulsar «Publicar lote»: continuará con las unidades que falten.
              </div>
            )}

            {/* Beneficio */}
            {lot.profit && (
              <div className="grid grid-cols-4 gap-3">
                <div className="bg-gray-50 rounded-lg p-3"><div className="text-xs text-gray-500">Unidades</div><div className="text-lg font-bold">{lot.profit.units_total}</div></div>
                <div className="bg-gray-50 rounded-lg p-3"><div className="text-xs text-gray-500">Vendidas</div><div className="text-lg font-bold">{lot.profit.units_sold}</div></div>
                <div className="bg-gray-50 rounded-lg p-3"><div className="text-xs text-gray-500">Ingresos</div><div className="text-lg font-bold">{lot.profit.revenue.toFixed(2)} €</div></div>
                <div className="bg-green-50 rounded-lg p-3"><div className="text-xs text-gray-500">Beneficio</div><div className="text-lg font-bold text-green-700">{lot.profit.profit.toFixed(2)} €</div></div>
              </div>
            )}

            {/* Unidades */}
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-500 text-left">
                  <tr>
                    <th className="px-3 py-2">Ref / Pegatina</th>
                    <th className="px-3 py-2">Modelo</th>
                    <th className="px-3 py-2">Taras</th>
                    <th className="px-3 py-2 text-center">Fotos</th>
                    <th className="px-3 py-2 text-right">Coste</th>
                    <th className="px-3 py-2 text-right">Precio</th>
                    <th className="px-3 py-2 text-center">Estado</th>
                    {isDraft && <th className="px-3 py-2 text-center">Acciones</th>}
                  </tr>
                </thead>
                <tbody>
                  {(lot.units || []).map((u) => (
                    <tr key={u.id} className="border-t border-gray-100">
                      <td className="px-3 py-2 font-mono text-xs">{u.unit_reference}<div className="text-gray-400">#{u.sticker_id}</div></td>
                      <td className="px-3 py-2 text-gray-600">{u.model || lot.model || '—'}</td>
                      <td className="px-3 py-2 text-gray-600 max-w-xs truncate">{u.defects || '—'}</td>
                      <td className="px-3 py-2 text-center">
                        <span className="inline-flex items-center gap-1 text-gray-600"><ImageIcon className="w-3 h-3" />{u.images_count ?? 0}</span>
                      </td>
                      <td className="px-3 py-2 text-right">{u.unit_cost?.toFixed(2)} €</td>
                      <td className="px-3 py-2 text-right font-medium">{u.unit_price?.toFixed(2)} €</td>
                      <td className="px-3 py-2 text-center"><StatusBadge status={u.status} /></td>
                      {isDraft && (
                        <td className="px-3 py-2">
                          <div className="flex items-center justify-center gap-1">
                            <button title="Editar" onClick={() => openEdit(u)}
                              className="p-1.5 text-gray-500 hover:text-primary-600 hover:bg-gray-100 rounded"><Pencil className="w-4 h-4" /></button>
                            <button title="Eliminar" onClick={() => { if (confirm(`¿Eliminar la unidad #${u.sticker_id}?`)) deleteUnitMut.mutate(u.id) }}
                              className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-gray-100 rounded"><Trash2 className="w-4 h-4" /></button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                  {(!lot.units || lot.units.length === 0) && (
                    <tr><td colSpan={isDraft ? 8 : 7} className="px-3 py-8 text-center text-gray-400">Sin unidades. Añade equipos al lote.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </Modal>

      {/* Añadir unidad */}
      <Modal isOpen={showAddUnit} onClose={() => setShowAddUnit(false)} title="Añadir unidad" size="lg">
        <div className="space-y-3">
          <div className="rounded-lg border border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800 p-3 text-sm text-gray-600 dark:text-gray-300">
            {lot?.model || lot?.specs || lot?.keyboard_layout
              ? <>El modelo, las especificaciones y el teclado vienen <span className="font-medium">prerrellenos con la plantilla del lote</span>. Edítalos si este equipo concreto es distinto.</>
              : <>El lote no tiene plantilla. <span className="font-medium">Indica el modelo, las especificaciones (CPU, RAM, disco…) y el teclado de este equipo.</span></>}
          </div>
          <UnitFields form={unitForm} setForm={setUnitForm} suggestedPrice={suggestedPrice} />
          <PhotoPicker files={files} setFiles={setFiles} />
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={() => setShowAddUnit(false)}>Cerrar</Button>
            <Button leftIcon={<Tag className="w-4 h-4" />} loading={addUnitMut.isPending} disabled={!unitForm.sticker_id}
              onClick={() => addUnitMut.mutate()}>Guardar y añadir otra</Button>
          </div>
        </div>
      </Modal>

      {/* Editar unidad */}
      <Modal isOpen={editUnit != null} onClose={() => setEditUnit(null)} title={`Editar unidad #${editUnit?.sticker_id || ''}`} size="lg">
        {editUnit && (
          <div className="space-y-3">
            <UnitFields form={editForm} setForm={setEditForm} suggestedPrice={undefined} />
            {/* Fotos ya guardadas */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Fotos guardadas</label>
              {editUnit.images_count > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {Array.from({ length: editUnit.images_count }).map((_, i) => (
                    <DraftThumb key={i} unitId={editUnit.id} idx={i}
                      onDelete={() => deleteImageMut.mutate({ unitId: editUnit.id, idx: i })} />
                  ))}
                </div>
              ) : <p className="text-xs text-gray-400">Sin fotos todavía.</p>}
            </div>
            {/* Añadir fotos nuevas */}
            <PhotoPicker files={editFiles} setFiles={setEditFiles} />
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="ghost" onClick={() => setEditUnit(null)}>Cancelar</Button>
              <Button loading={updateUnitMut.isPending} disabled={!editForm.sticker_id}
                onClick={() => updateUnitMut.mutate()}>Guardar cambios</Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  )
}

// Campos compartidos por los modales de alta y edición de unidad
function UnitFields({ form, setForm, suggestedPrice }: {
  form: UnitForm; setForm: (f: UnitForm) => void; suggestedPrice?: number
}) {
  return (
    <>
      <Input label="ID de pegatina" value={form.sticker_id} onChange={(e) => setForm({ ...form, sticker_id: e.target.value })} placeholder="001" />
      <Input label="Modelo de este equipo" value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })}
        placeholder="HP ProBook 640 G8" helperText="Se aplica a la ficha de esta unidad. Si lo dejas vacío se usará el nombre del lote." />
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Especificaciones de este equipo</label>
        <textarea className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" rows={2}
          value={form.specs} onChange={(e) => setForm({ ...form, specs: e.target.value })}
          placeholder="i7-1165G7 · 16 GB · 512 GB SSD NVMe · 14&quot; · Windows 11" />
        <p className="text-xs text-gray-500 mt-1">CPU, RAM, disco, pantalla… de este portátil. Se muestra en su ficha.</p>
      </div>
      <Input label="Teclado de este equipo" value={form.keyboard_layout} onChange={(e) => setForm({ ...form, keyboard_layout: e.target.value })}
        placeholder="Español (QWERTY) · Francés (AZERTY)…" helperText="Distribución/idioma del teclado de esta unidad." />
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Taras / desperfectos</label>
        <textarea className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" rows={2}
          value={form.defects} onChange={(e) => setForm({ ...form, defects: e.target.value })}
          placeholder="Esquina golpeada, lector de tarjetas KO, pantalla rayada" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Input label="Coste imputado (€)" type="number" value={form.unit_cost}
          onChange={(e) => setForm({ ...form, unit_cost: e.target.value === '' ? '' : Number(e.target.value) })} />
        <Input label={suggestedPrice != null ? `Precio venta (€) · sugerido ${suggestedPrice}` : 'Precio venta (€)'} type="number" value={form.unit_price}
          onChange={(e) => setForm({ ...form, unit_price: e.target.value === '' ? '' : Number(e.target.value) })}
          placeholder={suggestedPrice != null ? String(suggestedPrice) : ''} />
        <Input label="Garantía (meses, opcional)" type="number" value={form.warranty_months}
          onChange={(e) => setForm({ ...form, warranty_months: e.target.value === '' ? '' : Number(e.target.value) })} />
        <label className="flex items-center gap-2 text-sm mt-6">
          <input type="checkbox" checked={form.charger_included} onChange={(e) => setForm({ ...form, charger_included: e.target.checked })} />
          Incluye cargador
        </label>
      </div>
    </>
  )
}
